import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Paper, Stack,
  TextField, Tooltip, Typography,
} from '@mui/material';
import { useAuth } from '../auth/AuthContext';
import { PageHeader, AddressWithCopy } from '../components/common';
import { api, ApiError } from '../api/client';
import type { KeystoreStatus, Wallet } from '../api/types';

export function MyPage() {
  const { me } = useAuth();
  if (!me) return null;
  return (
    <Box>
      <PageHeader crumb="공통 · 마이페이지" title="마이페이지" desc="회원 정보와 계정 상태입니다." />
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h2" mb={2}>회원 정보</Typography>
            <Stack spacing={1.5}>
              <Row k="아이디" v={me.loginId} />
              <Row k="이름" v={me.name} />
              <Row k="국가코드" v={me.countryCode} />
              <Row k="상태" v={<Chip size="small" color="success" label={me.status} />} />
              <Row k="역할" v={me.roles.join(', ') || '—'} />
              <Row k="식별자(uid)" v={<code style={{ fontSize: 12 }}>{me.userUid.slice(0, 18)}…</code>} />
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <WalletVerification />
        </Grid>
        <Grid item xs={12} md={6}>
          <ChangePassword />
        </Grid>
        <Grid item xs={12} md={6}>
          <KeystoreDelivery />
        </Grid>
      </Grid>
    </Box>
  );
}

/** 온체인 적격성(isVerified) 배지 */
export function VerifiedBadge({ verified }: { verified: boolean | null }) {
  if (verified === null)
    return <Tooltip title="체인 조회 불가"><Chip size="small" label="확인 불가" sx={{ fontWeight: 700 }} /></Tooltip>;
  return verified
    ? <Chip size="small" color="success" label="✓ 검증됨 (isVerified)" sx={{ fontWeight: 700 }} />
    : <Chip size="small" color="warning" label="미검증" sx={{ fontWeight: 700 }} />;
}

function WalletVerification() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['me', 'wallets'],
    queryFn: () => api.get<Wallet[]>('/users/me/wallets'),
  });
  const active = (data ?? []).filter((w) => w.keyStatus === 'ACTIVE');

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h2" mb={2}>지갑 · 온체인 검증</Typography>
      {isLoading && <CircularProgress size={22} />}
      {error && <Typography color="error" variant="body2">불러오기 실패</Typography>}
      {!isLoading && !error && active.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          발급된 지갑이 없습니다. (가입 승인 시 지갑 생성·온체인 등록됩니다)
        </Typography>
      )}
      <Stack spacing={1.5}>
        {active.map((w) => (
          <Stack key={w.id} direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Stack>
              <AddressWithCopy address={w.walletAddress} />
              <Typography variant="caption" color="text.secondary">{w.keyType}</Typography>
            </Stack>
            <VerifiedBadge verified={w.verified} />
          </Stack>
        ))}
      </Stack>
      {active.some((w) => w.verified === false) && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          미검증 지갑은 토큰 전송·청약 신청이 제한됩니다. 관리자에게 KYC 등록을 요청하세요.
        </Alert>
      )}
    </Paper>
  );
}

function ChangePassword() {
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const submit = async () => {
    setMsg(null);
    if (newPw.length < 8) return setMsg({ ok: false, text: '새 비밀번호는 8자 이상이어야 합니다.' });
    if (newPw !== confirmPw) return setMsg({ ok: false, text: '새 비밀번호 확인이 일치하지 않습니다.' });
    setBusy(true);
    try {
      await api.patch('/users/me/password', { currentPw, newPw });
      setMsg({ ok: true, text: '비밀번호가 변경되었습니다.' });
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (e) {
      setMsg({ ok: false, text: e instanceof ApiError ? e.message : '변경에 실패했습니다.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h2" mb={2}>비밀번호 변경</Typography>
      <Stack spacing={2}>
        {msg && <Alert severity={msg.ok ? 'success' : 'error'}>{msg.text}</Alert>}
        <TextField label="현재 비밀번호" type="password" value={currentPw}
          onChange={(e) => setCurrentPw(e.target.value)} fullWidth autoComplete="current-password" />
        <TextField label="새 비밀번호 (8자 이상)" type="password" value={newPw}
          onChange={(e) => setNewPw(e.target.value)} fullWidth autoComplete="new-password" />
        <TextField label="새 비밀번호 확인" type="password" value={confirmPw}
          onChange={(e) => setConfirmPw(e.target.value)} fullWidth autoComplete="new-password" />
        <Button variant="contained" onClick={submit}
          disabled={busy || !currentPw || !newPw || !confirmPw}>
          {busy ? '변경 중…' : '비밀번호 변경'}
        </Button>
      </Stack>
    </Paper>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography color="text.secondary">{k}</Typography>
      <Typography component="div" fontWeight={600}>{v}</Typography>
    </Stack>
  );
}


/**
 * Type1 KeyStore 1회 인도 (SFR-002, A-8).
 *
 * 서버는 봉인본을 보관만 하고, 본인이 여기서 한 번 내려받으면 즉시 파기한다.
 * 그래서 다운로드는 **되돌릴 수 없는 조치**로 다루고, 확인 절차를 한 단계 둔다.
 */
function KeystoreDelivery() {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['keystore-status'],
    queryFn: () => api.get<KeystoreStatus>('/users/me/keystore'),
  });

  const download = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.post<{ walletAddress: string; keystore: string }>('/users/me/keystore/claim', {});
      // 서버에는 더 이상 사본이 없다 — 받은 즉시 파일로 내려준다
      const blob = new Blob([r.keystore], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `keystore-${r.walletAddress}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setDone(true);
      setConfirm(false);
      void qc.invalidateQueries({ queryKey: ['keystore-status'] });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'KeyStore 인도 실패');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h2" mb={2}>KeyStore (Type1)</Typography>
      {isLoading && <CircularProgress size={22} />}

      {!isLoading && !data?.exists && (
        <Typography variant="body2" color="text.secondary">
          인도 대상 KeyStore가 없습니다. Type2 사용자는 서비스가 검증필 KMS로 개인키를 보관하고 서명을 대행하므로
          별도 인도 절차가 없습니다.
        </Typography>
      )}

      {data?.exists && data.available && (
        <Stack spacing={2}>
          <Alert severity="warning">
            KeyStore(V3)는 <b>단 한 번만</b> 내려받을 수 있습니다. 다운로드하는 즉시 서버의 사본은 파기되어
            재발급 외에는 복구할 수 없습니다. 안전한 곳에 보관하십시오.
          </Alert>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary">지갑</Typography>
            <AddressWithCopy address={data.walletAddress} />
          </Stack>
          {err && <Alert severity="error">{err}</Alert>}
          <Box><Button variant="contained" onClick={() => setConfirm(true)}>KeyStore 다운로드</Button></Box>
        </Stack>
      )}

      {data?.exists && !data.available && (
        <Stack spacing={1.5}>
          {done && <Alert severity="success">KeyStore를 내려받았습니다. 서버 사본은 파기되었습니다.</Alert>}
          <Chip size="small" color="default" label="인도 완료 · 재조회 불가" sx={{ fontWeight: 700, alignSelf: 'flex-start' }} />
          <Typography variant="body2" color="text.secondary">
            {data.deliveredAt ? `${new Date(data.deliveredAt).toLocaleString('ko-KR')}에 인도되었습니다.` : '이미 인도되었습니다.'}
            {' '}분실 시 관리자에게 <b>키 교체</b>를 요청하면 새 지갑으로 재발급됩니다.
          </Typography>
        </Stack>
      )}

      <Dialog open={confirm} onClose={() => setConfirm(false)} maxWidth="xs" fullWidth>
        <DialogTitle>KeyStore 다운로드</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            지금 내려받으면 서버 사본이 <b>영구 파기</b>됩니다. 파일과 비밀번호를 모두 분실하면 지갑을 복구할 수 없습니다.
            계속하시겠습니까?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(false)}>취소</Button>
          <Button variant="contained" onClick={download} disabled={busy}>다운로드</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
